"use client";

import type { z } from "zod/v4";
import { ApiError } from "../../api-client";
import { getClientToken, refreshClientToken } from "../../auth";

// BIS routes ride the shared API host under /bis (Caddy path-routes /bis/* to the
// onsand-bis service); local default is the service's own port.
export const BIS_API_URL =
	process.env.NEXT_PUBLIC_BIS_API_URL ??
	(process.env.NEXT_PUBLIC_API_URL
		? `${process.env.NEXT_PUBLIC_API_URL}/bis`
		: "http://localhost:8086/bis");

// `new Headers(init)` accepts a Headers instance, a tuple list, or a record;
// spreading a Headers instance yields {} and silently drops every header.
function authHeaders(init?: HeadersInit): Headers {
	const headers = new Headers(init);
	if (!headers.has("Content-Type")) {
		headers.set("Content-Type", "application/json");
	}
	const token = getClientToken();
	if (token && !headers.has("Authorization")) {
		headers.set("Authorization", `Bearer ${token}`);
	}
	return headers;
}

async function fetchWithRetry(url: string, options?: RequestInit): Promise<Response> {
	const res = await fetch(url, {
		...options,
		headers: authHeaders(options?.headers),
	});

	if (res.status === 401) {
		const newToken = await refreshClientToken();
		if (newToken) {
			return fetch(url, {
				...options,
				headers: authHeaders(options?.headers),
			});
		}
	}

	return res;
}

export async function fetchApi<T>(
	path: string,
	schema: z.ZodType<T>,
	options?: RequestInit,
): Promise<T> {
	const res = await fetchWithRetry(`${BIS_API_URL}${path}`, options);

	if (!res.ok) {
		const body = await res.text();
		throw new ApiError(res.status, body);
	}

	const json = await res.json();
	return schema.parse(json);
}
